import React from "react";
import { Link } from "react-router-dom";
import { Navbar, Footer } from "@/components/yc/Nav";
import { Check } from "lucide-react";

const CHECKS = [
    {
        n: "01", h: "Portfolio", q: "Can they actually do the work?",
        p: "A showreel or portfolio of at least six real pieces, for clients or self-initiated. We ask one question: could this work go to a client today without rework?",
    },
    {
        n: "02", h: "Originality", q: "Is the work genuinely theirs?",
        p: "Every applicant uploads an original source file, such as a layered design file or raw footage, and signs an originality declaration. No AI-generated posters or visuals presented as original design. Anyone found breaking this rule is removed.",
    },
    {
        n: "03", h: "Rate card", q: "Do they understand their own value?",
        p: "A clear rate card showing what they charge and what's included. Creatives who can explain their pricing are easier to hire and easier to work with.",
    },
    {
        n: "04", h: "Service understanding", q: "Do they know how to deliver?",
        p: "Every applicant answers questions about their process, deliverables, turnaround times and usage rights. How they respond, including speed, clarity and tone, tells us how they'll handle a real brief.",
    },
    {
        n: "05", h: "Verified and UK based", q: "Are they who they say they are?",
        p: "Identity confirmed, location verified and consent given for verified businesses to contact them.",
    },
];

const CREATOR_SCORE = [
    { l: "Portfolio and showreel quality", v: 30 },
    { l: "Specialism depth", v: 20 },
    { l: "Service understanding", v: 20 },
    { l: "Training or equivalent paid experience", v: 15 },
    { l: "Pricing clarity", v: 15 },
];

const HEALTH_PILLARS = ["Punctuality", "Consistency", "Originality", "Safety", "Performance"];

const HowWeVet = () => {
    return (
        <div className="min-h-screen bg-yc-cream">
            <Navbar />

            <div className="max-w-5xl mx-auto px-6 md:px-8 pb-20" data-testid="how-we-vet-page">

                {/* HERO */}
                <section className="pt-16 pb-12 max-w-3xl">
                    <span className="yc-badge bg-yc-pink text-white">The Vetting Standard</span>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mt-5">
                        Not everyone with a portfolio earns a place.
                    </h1>
                    <p className="text-lg text-yc-ink/75 leading-relaxed mt-6">
                        We&apos;ve spent 14 years in the creative industry. We know the difference between real craft and a good-looking feed. This is how we decide who joins our network, and why it matters to every business that hires through it.
                    </p>
                </section>

                {/* WHY THIS EXISTS */}
                <section className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10" data-testid="why-this-exists">
                    <div className="md:col-span-5">
                        <span className="yc-overline">Why This Exists</span>
                        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight mt-3">
                            Hiring creative talent became a gamble.
                        </h2>
                    </div>
                    <div className="md:col-span-7 space-y-4 text-yc-ink/80 leading-relaxed">
                        <p>Design tools got cheaper, AI image generators arrived, and everyone with a laptop became a designer, illustrator or videographer overnight. For businesses trying to hire, telling real skill from polished noise became almost impossible.</p>
                        <p>Briefs went sideways. Deliverables missed the mark. Budgets were wasted on work that had to be redone. Creatives who had genuinely trained, who understood typography, composition, lighting, motion and storytelling, got buried in the noise.</p>
                        <p>The industry grew faster than it built any standards. We&apos;re building them now.</p>
                    </div>
                </section>

                {/* OUR AUTHORITY */}
                <section className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 items-start" data-testid="authority">
                    <div className="md:col-span-5">
                        <div className="yc-card bg-yc-blue text-white p-8 inline-flex flex-col items-center text-center w-full">
                            <div className="font-heading font-black text-7xl md:text-8xl tracking-tighter leading-none">14</div>
                            <div className="text-white/90 mt-2 font-semibold uppercase tracking-wide text-sm">years in the creative industry</div>
                        </div>
                    </div>
                    <div className="md:col-span-7">
                        <span className="yc-overline">Our Authority</span>
                        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight mt-3">
                            14 years of knowing the difference.
                        </h2>
                        <p className="text-yc-ink/80 leading-relaxed mt-4">
                            Our Creative Director has spent over 14 years across graphic design, brand identity, photography, video and creative direction: reviewing portfolios, leading campaigns and building creative teams from scratch. That judgment is now a documented, repeatable framework, consistent enough to be handed to any senior specialist and produce the same result. That&apos;s what separates a standard from a personal preference.
                        </p>
                    </div>
                </section>

                <div className="h-0.5 bg-yc-ink/10 my-8" aria-hidden="true" />

                {/* HOW WE VET */}
                <section className="py-6">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <span className="yc-overline">How We Vet</span>
                        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight mt-3">Five checks. Miss one and you don&apos;t get in.</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="checks-list">
                        {CHECKS.map((c) => (
                            <div key={c.n} className="yc-card bg-white p-7" data-testid={`check-${c.n}`}>
                                <div className="font-heading font-black text-4xl text-yc-blue leading-none tracking-tighter">{c.n}</div>
                                <h3 className="font-heading text-xl font-bold mt-4">{c.h}</h3>
                                <p className="text-sm font-semibold text-yc-ink/60 mt-1">{c.q}</p>
                                <p className="text-yc-ink/75 leading-relaxed text-[15px] mt-3">{c.p}</p>
                            </div>
                        ))}
                        <div className="yc-card bg-yc-pink text-white p-7 flex flex-col justify-center" data-testid="pass-all-five">
                            <h3 className="font-heading text-xl font-bold">Pass all five, then you&apos;re scored.</h3>
                            <p className="text-white/90 leading-relaxed text-[15px] mt-3">Only creatives who clear every check move on to receive their Curated Creator Score.</p>
                        </div>
                    </div>
                </section>

                <div className="h-0.5 bg-yc-ink/10 my-8" aria-hidden="true" />

                {/* HOW WE SCORE */}
                <section className="py-6">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <span className="yc-overline">How We Score</span>
                        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight mt-3">Every creative carries two scores.</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Curated Creator Score */}
                        <div className="yc-card bg-white p-8" data-testid="creator-score">
                            <h3 className="font-heading text-2xl font-black tracking-tighter">Curated Creator Score</h3>
                            <p className="text-sm font-semibold text-yc-ink/60 mt-1">Can they do the job?</p>
                            <p className="text-sm italic text-yc-ink/55 mt-1">Set by our team at vetting and reviewed every year.</p>
                            <div className="mt-5 divide-y divide-yc-ink/10">
                                {CREATOR_SCORE.map((r) => (
                                    <div key={r.l} className="flex items-center justify-between py-3">
                                        <span className="text-sm text-yc-ink/80">{r.l}</span>
                                        <span className="font-heading font-black text-yc-blue">{r.v}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-sm text-yc-ink/75 leading-relaxed mt-5">
                                The pass mark is 65 out of 100. Formal training and years of paid client work score equally, so self-taught talent is never shut out. Pricing is scored on clarity, not on being cheap.
                            </p>
                        </div>

                        {/* Health Score */}
                        <div className="yc-card bg-white p-8 flex flex-col" data-testid="health-score">
                            <h3 className="font-heading text-2xl font-black tracking-tighter">Health Score</h3>
                            <p className="text-sm font-semibold text-yc-ink/60 mt-1">Will they deliver?</p>
                            <p className="text-sm italic text-yc-ink/55 mt-1">Earned over time from real work.</p>
                            <div className="flex flex-wrap gap-2 mt-5">
                                {HEALTH_PILLARS.map((p) => (
                                    <span key={p} className="text-xs font-bold uppercase tracking-wide py-1.5 px-3 rounded-full border-2 border-yc-ink">{p}</span>
                                ))}
                            </div>
                            <p className="text-sm text-yc-ink/75 leading-relaxed mt-5">
                                Every business that hires through Curated Chaos rates the creatives it works with, so the score reflects real jobs, not promises.
                            </p>
                            <div className="mt-auto pt-5">
                                <p className="font-heading font-bold text-yc-ink leading-snug">Scores can never be bought. No plan, fee or upgrade can raise a creative&apos;s score.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* WHAT THIS MEANS FOR YOUR BUSINESS */}
                <section className="py-6">
                    <div className="yc-card bg-yc-blue text-white border-yc-ink p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10" data-testid="business-section">
                        <div>
                            <span className="yc-overline text-white">What This Means For Your Business</span>
                            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight mt-3 text-white">Hire with the checking already done.</h2>
                            <p className="text-white/90 leading-relaxed mt-4">
                                Every creative you find through your seat has already passed all five checks and carries both scores. Filter by discipline, location, rate, availability and score, then hire directly with no agency mark-up or commission.
                            </p>
                            <Link to="/brands/join" data-testid="business-cta" className="yc-btn-primary bg-white text-yc-ink mt-7">Get your team a seat</Link>
                        </div>
                        <div className="flex flex-col justify-center gap-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="rounded-2xl border border-white/20 bg-white/5 p-5">
                                    <div className="font-heading font-black text-4xl tracking-tighter">200+</div>
                                    <div className="text-sm text-white/70 mt-1">vetted creatives</div>
                                </div>
                                <div className="rounded-2xl border border-white/20 bg-white/5 p-5">
                                    <div className="font-heading font-black text-4xl tracking-tighter">UK wide</div>
                                    <div className="text-sm text-white/70 mt-1">every major city</div>
                                </div>
                            </div>
                            <p className="text-sm text-white/80 leading-relaxed">
                                Graphic designers, illustrators, animators, motion designers, photographers, videographers, social media managers and content creators.
                            </p>
                        </div>
                    </div>
                </section>

                {/* THE BIGGER PICTURE */}
                <section className="py-10 text-center max-w-3xl mx-auto" data-testid="bigger-picture">
                    <span className="yc-overline">The Bigger Picture</span>
                    <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight mt-3">Creative work is a craft. We treat it like one.</h2>
                    <p className="text-yc-ink/80 leading-relaxed mt-5">
                        The creative industry needed someone to raise the bar: for businesses that need people they can rely on, and for creatives who have put in the real work and deserve to be recognised for it. Our goal isn&apos;t only to build a better network. It&apos;s to set the standard the industry skipped when it grew too fast. If you&apos;ve earned your place, you&apos;ll know it when you get in.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/login?role=creator" data-testid="hwv-apply-cta" className="yc-btn-secondary">Apply as a creative</Link>
                        <Link to="/brands/join" data-testid="hwv-brand-cta" className="yc-btn-primary">Get your team a seat</Link>
                    </div>

                    <div className="mt-10 p-6 rounded-2xl border-[1.5px] border-yc-ink/15 text-left">
                        <p className="text-sm text-yc-ink/65 leading-relaxed">
                            <strong className="text-yc-ink flex items-center gap-2 mb-1"><Check className="w-4 h-4 text-yc-blue" /> A note on acceptance rates:</strong>
                            we track every application, including how many are reviewed, how many reach the final check and how many are accepted. Once we have enough applications for the figures to be meaningful, we&apos;ll publish them here. We believe in showing our work.
                        </p>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default HowWeVet;
